import type {ExecuteActionResponseADto} from '@cohort/admin-schemas/apps';
import type {QueryOptions} from '@cohort/merchants/hooks/api/Query';
import {useCohortQuery} from '@cohort/merchants/hooks/api/Query';
import {executeAction} from '@cohort/merchants/lib/api/Apps';
import type {GetContactAttributesActionStruct} from '@cohort/shared/apps/brevo/actions/getContactAttributes';
import type {GetContactListActionStruct} from '@cohort/shared/apps/brevo/actions/getContactList';
import type {GetContactListsActionStruct} from '@cohort/shared/apps/brevo/actions/getContactLists';
import type {UseQueryResult} from '@tanstack/react-query';

export const brevoAppKeys = {
  brevoApp: ['brevoApp'] as const,
  getContactAttributes: (merchantId: string, merchantConnectionId: string) =>
    [...brevoAppKeys.brevoApp, merchantId, merchantConnectionId, 'get-contact-attributes'] as const,
  getContactLists: (merchantId: string, merchantConnectionId: string) =>
    [...brevoAppKeys.brevoApp, merchantId, merchantConnectionId, 'get-contact-lists'] as const,
  getContactList: (merchantId: string, merchantConnectionId: string, listId: number) =>
    [
      ...brevoAppKeys.brevoApp,
      merchantId,
      merchantConnectionId,
      'get-contact-list',
      listId,
    ] as const,
};

export const useBrevoContactAttributes = (
  merchantId: string,
  merchantConnectionId: string,
  options?: QueryOptions
): UseQueryResult<ExecuteActionResponseADto<GetContactAttributesActionStruct>, unknown> => {
  return useCohortQuery({
    queryKey: brevoAppKeys.getContactAttributes(merchantId, merchantConnectionId),
    queryFn: async () => {
      return executeAction<GetContactAttributesActionStruct>(merchantId, {
        appId: 'brevo',
        actionId: 'get-contact-attributes',
        merchantConnectionId,
        input: {},
      });
    },
    ...options,
  });
};

export const useBrevoContactLists = (
  merchantId: string,
  merchantConnectionId: string,
  options?: QueryOptions
): UseQueryResult<ExecuteActionResponseADto<GetContactListsActionStruct>, unknown> => {
  return useCohortQuery({
    queryKey: brevoAppKeys.getContactLists(merchantId, merchantConnectionId),
    queryFn: async () => {
      return executeAction<GetContactListsActionStruct>(merchantId, {
        appId: 'brevo',
        actionId: 'get-contact-lists',
        merchantConnectionId,
        input: {},
      });
    },
    ...options,
  });
};

export const useBrevoContactList = (
  merchantId: string,
  merchantConnectionId: string,
  listId: number,
  options?: QueryOptions
): UseQueryResult<ExecuteActionResponseADto<GetContactListActionStruct>, unknown> => {
  return useCohortQuery({
    queryKey: brevoAppKeys.getContactList(merchantId, merchantConnectionId, listId),
    queryFn: async () => {
      return executeAction<GetContactListActionStruct>(merchantId, {
        appId: 'brevo',
        actionId: 'get-contact-list',
        merchantConnectionId,
        input: {listId},
      });
    },
    ...options,
  });
};
